import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Vehicle } from '@/features/vehicles/vehicle.schema';
import { Customer } from '@/features/customers/customer.schema';
import { logger } from "@/lib/logger";

/**
 * Fetch vehicle display label
 * Returns: "Make Model (Plate)" or fallback
 * 
 * @example
 * vehicleLabel('veh_123') // "VW Golf (XX-123-Y)"
 */
export async function vehicleLabel(id: string, teamId: string): Promise<string> {
  try {
    const docRef = doc(db, 'teams', teamId, 'vehicles', id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return `Vehicle (${id.slice(0, 8)})`;
    }
    
    const vehicle = docSnap.data() as Vehicle;
    const brand = vehicle.brand || '';
    const model = vehicle.model || '';
    const plate = vehicle.licensePlate || '';
    
    if (brand && model && plate) {
      return `${brand} ${model} (${plate})`;
    } else if (brand && model) {
      return `${brand} ${model}`;
    } else if (plate) {
      return plate;
    } else {
      return `Vehicle (${id.slice(0, 8)})`;
    }
  } catch (error) {
    logger.error('Failed to fetch vehicle label:', error);
    return `Vehicle (${id.slice(0, 8)})`;
  }
}

/**
 * Fetch customer display label
 * Returns: "Name" or "Email" or fallback
 * 
 * @example
 * customerLabel('cust_456') // "John Doe"
 */
export async function customerLabel(id: string, teamId: string): Promise<string> {
  try {
    const docRef = doc(db, 'teams', teamId, 'customers', id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return `Customer (${id.slice(0, 8)})`;
    }
    
    const customer = docSnap.data() as Customer;
    
    // Use displayName if available, otherwise construct from fields
    if (customer.displayName) {
      return customer.displayName;
    }
    
    // Construct name based on type
    if (customer.type === 'company' && customer.companyName) {
      return customer.companyName;
    } else if (customer.firstName && customer.lastName) {
      return `${customer.firstName} ${customer.lastName}`;
    } else if (customer.firstName) {
      return customer.firstName;
    } else if (customer.email) {
      return customer.email;
    } else {
      return `Customer (${id.slice(0, 8)})`;
    }
  } catch (error) {
    logger.error('Failed to fetch customer label:', error);
    return `Customer (${id.slice(0, 8)})`;
  }
}

/**
 * Fetch invoice display label
 * Returns: "Invoice #123 - Customer" or fallback
 * 
 * @example
 * invoiceLabel('inv_789') // "Invoice #2024-001"
 */
export async function invoiceLabel(
  id: string,
  teamId: string,
  type: 'sales' | 'purchase'
): Promise<string> {
  try {
    const collection = type === 'sales' ? 'salesInvoices' : 'purchaseInvoices';
    const docRef = doc(db, 'teams', teamId, collection, id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return `Invoice (${id.slice(0, 8)})`;
    }
    
    const invoice = docSnap.data();
    const invoiceNo = invoice.invoiceNo || invoice.invoiceNumber || '';
    
    if (invoiceNo) {
      return `Invoice #${invoiceNo}`;
    } else {
      return `Invoice (${id.slice(0, 8)})`;
    }
  } catch (error) {
    logger.error('Failed to fetch invoice label:', error);
    return `Invoice (${id.slice(0, 8)})`;
  }
}
